import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Field, Form, Formik } from 'formik'
import { Alert } from '@wellbeingxltd/fe-lib/components'
import { GET_STORE } from '@wellbeingxltd/fe-lib/constants'
import { transformShopifyProducts } from '@wellbeingxltd/fe-lib/helpers'
import { Button } from 'components'
import { ToggleButton } from 'components/ToggleButton'
import { useSupplierProfile } from 'hooks/useSupplierProfile'
import ResourcesActions from 'store/models/resources'
import { SelectField } from './SelectField'

// Get options with shopifyProductId, shopifyVariantId
const getOptions = normilizedData => {
    return normilizedData.map(
        ({ id, image, title, 'variant-title': variantName, 'variant-price': variantPrice, 'variant-id': variantId, 'variant-sku': sku }) => ({
            value: variantId,
            label: `${title} -> ${variantName} -> £${variantPrice}`,
            shopifyProductId: id,
            imageSrc: image?.src,
            sku,
        })
    )
}
export const ProductForm = ({ product, resourceId }) => {
    const dispatch = useDispatch()
    const { updateShopifyResourceIdLoading } = useSelector(GET_STORE.RESOURCES)
    const { shopifyProducts, supplierId } = useSupplierProfile()

    const productsWithShopifyData = transformShopifyProducts(shopifyProducts)
    const options = getOptions(productsWithShopifyData)

    const { productId, shopifyInventoryBehaviour, shopifyInventoryPolicy, shopifyProductId, shopifyVariantId, label } = product

    const initialValues = {
        resourceId,
        productId,
        supplierId,
        removeShopifyProducts: false,
        shopifyInventoryBehaviour: shopifyInventoryBehaviour ?? true,
        shopifyInventoryPolicy: shopifyInventoryPolicy ?? false,
        shopifyProduct: undefined, // used on FE
        shopifyProductId,
        shopifyVariantId,
        label,
    }

    const onSubmit = (values, actions) => {
        // Remove `shopifyProduct` field from body.
        const { shopifyProduct, ...rest } = values
        dispatch(
            ResourcesActions.updateShopifyResource(rest, (msg, type) => {
                Alert(msg, type)
                if (type === 'success') {
                    actions.setTouched({})
                }
            })
        )
    }

    const currentProductLoading = updateShopifyResourceIdLoading === productId

    return (
        <div className="productSelect contentHeight">
            <Formik enableReinitialize={true} initialValues={initialValues} onSubmit={onSubmit}>
                {({ dirty, touched, values }) => (
                    <>
                        <Form>
                            <div className="form">
                                <Field name="shopifyProduct" options={options} productId={productId} component={SelectField} />
                                <Button loading={currentProductLoading} type="submit" disabled={!dirty || !Object.keys(touched).length}>
                                    Save
                                </Button>
                            </div>

                            <Field name="shopifyInventoryBehaviour">
                                {({ field, form }) => (
                                    <ToggleButton
                                        {...{
                                            disabled: !values?.shopifyProduct || currentProductLoading,
                                            id: `${productId}-shopifyInventoryBehavior`,
                                            label: 'Betterspace orders will decrement Shopify inventory',
                                            onChange: event => {
                                                form.setFieldValue(field.name, event.target.checked)
                                                form.setTouched(field.name)
                                            },
                                            checked: field.value,
                                        }}
                                    />
                                )}
                            </Field>
                            <Field name="shopifyInventoryPolicy">
                                {({ field, form }) => (
                                    <ToggleButton
                                        {...{
                                            disabled: !values?.shopifyProduct || currentProductLoading,
                                            id: `${productId}-shopifyInventoryPolicy`,
                                            label: 'Betterspace orders can be placed even if Shopify inventory is out of stock',
                                            onChange: event => {
                                                form.setFieldValue(field.name, event.target.checked)
                                                form.setTouched(field.name)
                                            },
                                            checked: field.value,
                                        }}
                                    />
                                )}
                            </Field>
                        </Form>
                        {values?.shopifyProduct && (
                            <>
                                <p>
                                    <b>Title:</b> {values?.shopifyProduct?.label}
                                </p>
                                <p>
                                    <b>SKU:</b> {values?.shopifyProduct?.sku}
                                </p>
                            </>
                        )}
                    </>
                )}
            </Formik>
        </div>
    )
}
